import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Product } from "../product-step/types";
import { MriScanCard } from "./MriScanCard";
import { AddOnSection } from "./AddOnSection";
import { MriPackageComparisonModal } from "./MriPackageComparisonModal";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { BookingLanguage } from "@/types/booking";
import { resolveProductKey, t as translate } from "@/locales";

import {
  trackMriComparisonOpen,
  trackMriScanSectionView,
  trackMriScanToggle,
} from "@/utils/analytics";

interface MriScanSectionProps {
  /** Active full-body MRI packages (no blood-only), sorted for display. */
  scanProducts: Product[];
  /** Package id of the scan currently attached to the membership, if any. */
  attachedScanId: string | null;
  onAttachScan: (packageId: string) => void;
  onDetachScan: () => void;
  /** The attached scan's own add-ons, shown in their own section below. */
  scanAddOns: Product[];
  selectedAddOns: string[];
  onAddOnToggle: (productId: string) => void;
  language: BookingLanguage;
}

/**
 * Membership flow: full-body MRI scans offered directly on the add-ons page,
 * in the same compact card format as every other add-on. Deliberately quiet
 * (single column, below the blood tests) — only ~4% of bookings attach a
 * scan, so it must not dominate the page. Radio behaviour: one scan max;
 * selecting attaches it to the membership (member pricing everywhere),
 * clicking the selected card detaches it again. The attached scan's own
 * add-ons unfold as a section beneath the grid, named after that scan.
 */
export const MriScanSection: React.FC<MriScanSectionProps> = ({
  scanProducts,
  attachedScanId,
  onAttachScan,
  onDetachScan,
  scanAddOns,
  selectedAddOns,
  onAddOnToggle,
  language,
}) => {
  // Advanced and Pro, not Core: both carry the blood test the membership
  // already covers, so the member-price story reads cleanly on them. Core
  // doesn't, and a three-way choice was too much for a ~4%-attach offer.
  // Core stays visible in the comparison table (at list price, since its
  // member rate doesn't absorb the CHF 299) — that's the one place a
  // membership-first user meets it, deliberately, and it stays out of this
  // grid even once chosen. Its add-ons still land, because they have their
  // own section below rather than tiles folded into a card.
  const offeredScans = scanProducts.filter(
    (p) => p.human_readable_id !== "core",
  );

  // The attached scan, whichever it is. Names the add-on section below, and
  // Core reaches it without ever appearing in the grid.
  const attachedScan = scanProducts.find((p) => p.id === attachedScanId);
  const attachedScanName = attachedScan
    ? attachedScan.translations?.name ||
      resolveProductKey(language, attachedScan.name)
    : "";
  // Attaching a scan re-filters the add-ons above this section (bundled blood
  // tests drop out), which would shift the page under the click. Anchor the
  // section: remember its viewport offset at click time, restore it after the
  // re-render so the card the user clicked stays put.
  const sectionRef = useRef<HTMLDivElement>(null);
  const anchorTopRef = useRef<number | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // The add-ons arrive together with attachedScanId (the product list refilters
  // on attach), so the block mounts collapsed and opens a frame later — that's
  // what makes the unfold animate instead of snapping in.
  const [addOnsOpen, setAddOnsOpen] = useState(false);
  useEffect(() => {
    if (attachedScanId && scanAddOns.length > 0) {
      const raf = requestAnimationFrame(() => setAddOnsOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    setAddOnsOpen(false);
  }, [attachedScanId, scanAddOns.length]);

  const rememberAnchor = () => {
    anchorTopRef.current =
      sectionRef.current?.getBoundingClientRect().top ?? null;
  };

  // The offer's impression count. Fired on intersection because the section
  // sits below the add-ons: mounting says nothing about whether it was seen,
  // and this number is what the removed `mri-crosssell` step_view used to
  // give us. Once per mount — the observer disconnects on the first hit.
  const viewedRef = useRef(false);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || viewedRef.current || offeredScans.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || viewedRef.current) return;
        viewedRef.current = true;
        observer.disconnect();
        trackMriScanSectionView(offeredScans.map((s) => s.name));
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [offeredScans]);

  const trackToggle = (
    scan: Product,
    attached: boolean,
    source: "section_card" | "comparison_modal",
  ) =>
    trackMriScanToggle(
      attached,
      scan.name,
      scan.default_price?.unit_amount ?? null,
      source,
    );

  useLayoutEffect(() => {
    const target = anchorTopRef.current;
    if (target === null) return;
    anchorTopRef.current = null;

    // The add-ons above fade out over ~280ms before they leave the layout, so
    // hold the anchor across those frames rather than correcting once. Any
    // real scroll input from the user ends it immediately.
    let raf = 0;
    let frames = 0;
    let cancelled = false;
    const stop = () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
    };
    const tick = () => {
      if (cancelled) return;
      const top = sectionRef.current?.getBoundingClientRect().top;
      if (top != null) {
        const delta = top - target;
        if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
      }
      // ~30 frames ≈ 500ms at 60fps, comfortably past the exit animation.
      if (++frames < 30) raf = requestAnimationFrame(tick);
      else stop();
    };
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    tick();
    return stop;
  }, [attachedScanId]);

  if (offeredScans.length === 0) return null;

  return (
    // Optional block, visually set apart: below the add-ons the page "cuts"
    // to a cooler blue-gray surface running the full viewport width (the
    // w-screen pseudo-element; BookingCalendar clips the horizontal
    // overflow). -mb-12 swallows the content container's bottom padding so
    // the band runs to the very end of the page.
    <div
      ref={sectionRef}
      className="relative isolate mt-10 mb-4 py-12 before:content-[''] before:absolute before:inset-y-0 before:left-1/2 before:-translate-x-1/2 before:w-screen before:-z-10 before:bg-blue-gray-100/60"
    >
      {/* Outcome-led section header, same scale as the page's "Select your
          add-ons" heading. */}
      {/* One step below the page heading (24px) so the hierarchy holds. */}
      <div className="text-center mb-6 px-4">
        <h2 className="text-xl font-medium text-neutral-900 normal-case">
          {translate(language, "addOnsStep.mriSectionTitle")}
        </h2>
        <p className="mt-2 text-sm text-neutral-900">
          {translate(language, "addOnsStep.mriSectionSubtitle")}
        </p>
      </div>

      {/* Same two-column rhythm as the other add-on sections, so the scan
          cards keep the familiar card proportions instead of full-width
          strips. Collapsed cards stretch to a shared height; once one is
          selected the add-on section below it opens and the pair stops
          stretching. */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${
          attachedScanId ? "items-start" : "items-stretch"
        }`}
      >
        {offeredScans.map((scan) => {
          const isAttached = scan.id === attachedScanId;
          // Catalog copy for Advanced opens "Everything in Core" — a tier this
          // section deliberately hides, so it has nothing to refer to. Swap in
          // a self-contained line that folds Core's scope into it. Pro keeps
          // its catalog copy: "Everything in Advanced" still resolves, because
          // Advanced is right there next to it.
          const descriptionOverride =
            scan.human_readable_id === "advanced"
              ? translate(language, "addOnsStep.mriScanDescriptionAdvanced")
              : undefined;
          return (
            <MriScanCard
              key={scan.id}
              product={scan}
              descriptionOverride={descriptionOverride}
              isSelected={isAttached}
              onToggle={() => {
                rememberAnchor();
                trackToggle(scan, !isAttached, "section_card");
                if (isAttached) onDetachScan();
                else onAttachScan(scan.id);
              }}
              language={language}
            />
          );
        })}
      </div>

      {/* Core isn't offered here, and the two tiers that are don't explain
          what they leave out. This is the way back to the full picture —
          quiet, centred under the grid, so it reads as a reference rather
          than a third option competing with the cards. It sits directly under
          the cards, BEFORE the attached scan's own add-ons: rendered after
          them it drifted to the bottom of the page the moment a scan was
          picked, which is nowhere near the packages it explains. */}
      <div className="mt-5 flex justify-center">
        {/* No pad behind the words — only the plus gets a disc, so the control
            reads as "opens something" without turning into a button that
            competes with the cards' own actions. */}
        <button
          type="button"
          onClick={() => {
            trackMriComparisonOpen(
              scanProducts.find((p) => p.id === attachedScanId)?.name ?? null,
            );
            setShowComparison(true);
          }}
          className="group/compare inline-flex items-center gap-2 text-sm font-normal normal-case text-neutral-700 transition-colors hover:text-neutral-900"
        >
          {translate(language, "addOnsStep.mriComparePackages")}
          <span className="inline-flex size-5 items-center justify-center rounded-full bg-neutral-900/[0.06] transition-colors group-hover/compare:bg-neutral-900/[0.12]">
            <PlusIcon size={12} strokeWidth={1.6} />
          </span>
        </button>
      </div>

      {/* The chosen scan's own add-ons, as their own section rather than tiles
          folded inside the card. Two reasons: they're the same kind of thing
          as the blood add-ons above, so they should look it — and a Core pick
          has nowhere to nest, because Core is never a card here. Animated with
          the 0fr→1fr grid-rows trick (height auto-animates, no magic
          max-height); mounts closed and flips open a frame later, which is
          what makes it unfold rather than appear. */}
      {scanAddOns.length > 0 && (
        <div
          className={`grid transition-all duration-300 ease-out ${
            addOnsOpen
              ? "mt-8 grid-rows-[1fr] opacity-100"
              : "mt-0 grid-rows-[0fr] opacity-0"
          }`}
          aria-hidden={!addOnsOpen}
        >
          <div className="min-h-0 overflow-hidden">
            <AddOnSection
              title={translate(language, "addOnsStep.mriScanAddOnsFor").replace(
                "{{scan}}",
                attachedScanName,
              )}
              products={scanAddOns}
              selectedAddOns={selectedAddOns}
              onAddOnToggle={onAddOnToggle}
              language={language}
            />
          </div>
        </div>
      )}

      <MriPackageComparisonModal
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
        language={language}
        scanProducts={scanProducts}
        attachedScanId={attachedScanId}
        onSelectScan={(id) => {
          rememberAnchor();
          const scan = scanProducts.find((p) => p.id === id);
          if (scan) trackToggle(scan, true, "comparison_modal");
          onAttachScan(id);
        }}
      />
    </div>
  );
};
